import {
  state,
  tempUsersData,
} from "../../app/state/state.js";
import {
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
  bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
  destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
  ADMIN_APP_BOARD_PERMISSIONS,
  COLLECTION_PERMISSION_OPTIONS,
  applyPermissionImplications,
  isPermissionLockedByParent,
} from "./permissionModel.js";
import { openUserForm } from "./userFormModal.js";
import {
  ensureDeactivationDraftSummariesLoaded,
  getDeactivationDraftSummary,
  handleUserDeactivationSettingsButtonClick,
  handleUserDeactivationSettingsChange,
  handleUserDeactivationSettingsSaveSuccess,
  renderDeactivationDraftContainer,
  resetUserDeactivationSettingsState,
  syncUserDeactivationSettingsStateFromDom,
  setupUserDeactivationDraftControls,
} from "./userDeactivationSettings.js";
import {
  handleUserInviteSettingsButtonClick,
  handleUserInviteSettingsChange,
  renderUserInvitesSection,
  resetUserInviteSettingsState,
} from "./userInviteSettings.js";

let showInactiveUsers = false;
let updateSaveButtonHandler = () => {};

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];
const PERMISSION_COLUMNS = COLLECTION_PERMISSION_OPTIONS.map((option) => ({
  key: option.value,
  label: option.label,
  impliedBy: option.impliedBy,
}));

function attrValue(value = "") {
  return escapeHTML(String(value ?? ""))
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Ensures admin users keep stored app-board permissions for consistency.
 * @param {object} user User settings draft record.
 * @returns {void}
 */
function normalizeAdminPermissions(user) {
  if (!user) {return;}
  if (user.role !== "admin") {
    user.permissions = applyPermissionImplications(user.permissions || []);
    return;
  }
  user.permissions = applyPermissionImplications([
    ...(Array.isArray(user.permissions) ? user.permissions : []),
    ...ADMIN_APP_BOARD_PERMISSIONS,
  ]);
}

function getUsersTabContainer() {
  return document.getElementById("settings-tab-content");
}

function rerenderUsersTab() {
  const container = getUsersTabContainer();
  if (!container) {
    return;
  }
  renderUserSettingsTab(container, {
    updateSaveButton: updateSaveButtonHandler,
  });
}

function isUserSettingsTabActive() {
  const usersTabButton = document.querySelector(
    '.settings-tab-btn[data-tab="users"]',
  );
  return Boolean(usersTabButton?.classList.contains("font-semibold"));
}

function renderUserSettingsCheckbox({
  checked = false,
  className = "",
  dataAttrs = "",
  disabled = false,
  label,
}) {
  const checkedAttr = checked ? " checked" : "";
  const disabledAttr = disabled ? " disabled" : "";
  const disabledClass = disabled ? " opacity-60 cursor-not-allowed" : "";
  return `
    <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} justify-center${disabledClass}" data-native-checkbox-label title="${attrValue(label)}">
      <input type="checkbox" class="${NATIVE_CHECKBOX_CLASSES} ${className}"${checkedAttr}${disabledAttr}${dataAttrs}>
      <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
      </span>
      <span class="sr-only">${escapeHTML(label)}</span>
    </label>
  `;
}

function setupUserRoleControls(container) {
  container.querySelectorAll("[data-user-row-role-control]").forEach((root) => {
    const mirrorInput = root.parentElement?.querySelector(".user-role-select");
    if (!(mirrorInput instanceof HTMLInputElement)) {return;}

    createSelectControl({
      root,
      mirrorInput,
      value: root.dataset.roleValue || "user",
      options: ROLE_OPTIONS,
      placeholder: "Select role",
      accessibleName: "User role",
      clearable: false,
      disabled: mirrorInput.disabled,
      portalMenu: true,
    });
  });
}

function getMobilePermissionSummary(user, isAdminUser) {
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  const permissionLabels = PERMISSION_COLUMNS
    .filter((column) => isAdminUser ||
      permissions.includes(column.key))
    .map((column) => column.label);

  const providerPrefix = user.isAppProvider === true ? ["Provider"] : [];
  const labels = [...providerPrefix, ...permissionLabels];
  return labels.length > 0 ? labels.join(", ") : "No permissions";
}

function getManagerLabel(managerUserId) {
  if (!managerUserId) {
    return "Manager not set";
  }
  if (managerUserId === "none") {
    return "No manager";
  }

  const manager = tempUsersData.find((user) => user.id === managerUserId);
  if (!manager) {
    return "Unknown manager";
  }

  const inactiveSuffix = manager.isActive === false ? " (inactive)" : "";
  return `${manager.name || manager.email || "Unknown manager"}${inactiveSuffix}`;
}

/**
 * Renders the full Users settings tab.
 * @param {HTMLElement} container Target container.
 * @param {{ updateSaveButton: Function }} options Render dependencies.
 */
export function renderUserSettingsTab(container, options = {}) {
  updateSaveButtonHandler = options.updateSaveButton || (() => {});
  ensureDeactivationDraftSummariesLoaded({
    rerenderUsersTab,
    shouldRerender: isUserSettingsTabActive,
  });
  destroyRegisteredControlsIn(container);

  const compactControlColumnClass = "settings-users-control-cell";
  const sortedUsers = [...tempUsersData].sort((a, b) => {
    const aInactive = a?.isActive === false ? 1 : 0;
    const bInactive = b?.isActive === false ? 1 : 0;
    if (aInactive !== bInactive) {
      return aInactive - bInactive;
    }
    return (a?.name || a?.email || "").localeCompare(b?.name || b?.email || "");
  });
  const visibleUsers = showInactiveUsers
    ? sortedUsers
    : sortedUsers.filter((user) => user?.isActive !== false);
  const adminCount = tempUsersData.filter(
    (user) => user.role === "admin" && user.isActive !== false,
  ).length;

  const rows = visibleUsers
    .map((user) => {
      const index = tempUsersData.findIndex((entry) => entry.id === user.id);
      const isLastAdmin = user.role === "admin" && adminCount <= 1;
      const isAdminUser = user.role === "admin";
      const perms = Array.isArray(user.permissions) ? user.permissions : [];
      const isAppProvider = user.isAppProvider === true;
      const isInactive = user.isActive === false;
      const activeDraft = getDeactivationDraftSummary(user.id);
      const accessDraft = getDeactivationDraftSummary(user.id, "access_change");
      const hasDraft = Boolean(activeDraft);
      const mobilePermissionSummary = getMobilePermissionSummary(user, isAdminUser);

      return `
        <tr class="border-b hover:bg-gray-50 transition-colors ${isInactive ? "opacity-70 bg-gray-50" : ""}" data-user-index="${index}">
            <td class="settings-users-user-cell py-3 px-4 align-top" data-mobile-label="User">
                <div class="settings-users-identity">
                    <div class="font-medium text-gray-900 break-words">${escapeHTML(user.name || "Unknown")}${isInactive ? ' <span class="text-xs font-normal text-gray-500">(inactive)</span>' : ""}</div>
                    <div class="text-xs text-gray-500 break-all">${escapeHTML(user.email || "")}</div>
                    <div class="mt-1 text-xs text-gray-500">Manager: ${escapeHTML(getManagerLabel(user.managerUserId))}</div>
                </div>
            </td>
            <td class="settings-users-role-cell py-3 px-3 align-top whitespace-nowrap" data-mobile-label="Role">
                <div data-user-row-role-control data-role-value="${attrValue(user.role || "user")}">
                </div>
                <input type="hidden" class="user-role-select" data-index="${index}" value="${attrValue(user.role || "user")}"${isLastAdmin ? ' disabled title="Cannot remove last admin"' : ""}>
            </td>
            <td class="settings-users-matrix-cell border-l border-gray-300 py-3 align-top text-center whitespace-nowrap ${compactControlColumnClass}" data-mobile-label="Provider">
                ${renderUserSettingsCheckbox({
                  checked: isAppProvider,
                  className: "user-app-provider-check",
                  dataAttrs: ` data-index="${index}"`,
                  label: "Provider",
                })}
            </td>
            ${PERMISSION_COLUMNS
              .map(
                (column) => `
            <td class="settings-users-matrix-cell border-l border-gray-300 py-3 align-top text-center whitespace-nowrap ${compactControlColumnClass}" data-mobile-label="${attrValue(column.label)}">
                ${renderUserSettingsCheckbox({
                  checked: perms.includes(column.key) || isAdminUser,
                  className: "user-perm-check",
                  dataAttrs: ` data-perm="${attrValue(column.key)}" data-index="${index}"`,
                  disabled: isAdminUser ||
                    isPermissionLockedByParent(user.permissions || [], column.key),
                  label: column.label,
                })}
            </td>`,
              )
              .join("")}
            <td class="settings-users-mobile-permissions-cell" data-mobile-label="Permissions">
                <span>${escapeHTML(mobilePermissionSummary)}</span>
            </td>
            <td class="settings-users-action-cell border-l border-gray-300 py-3 align-top text-center whitespace-nowrap" data-mobile-label="Actions">
                <div class="flex items-start justify-center gap-1">
                    ${accessDraft ? `<button type="button" class="btn-open-access-review standard-action-icon-btn" data-user-id="${attrValue(user.id)}" data-draft-id="${attrValue(accessDraft.id)}" title="Resume access review" aria-label="Resume access review"><span class="material-symbols-outlined pointer-events-none text-[18px]">manage_accounts</span></button>` : ""}
                    <button type="button" class="btn-edit-user standard-action-icon-btn" data-index="${index}" title="Edit user" aria-label="Edit user">
                        <span class="material-symbols-outlined pointer-events-none text-[18px]">edit</span>
                    </button>
                ${!isInactive ? `
                    <button type="button" class="btn-open-deactivation-draft standard-action-icon-btn ${hasDraft ? "standard-action-icon-btn--active-draft" : "standard-action-icon-btn--danger"}" data-user-id="${attrValue(user.id)}" title="${hasDraft ? "Resume deactivation draft" : "Deactivate user"}" aria-label="${hasDraft ? "Resume deactivation draft" : "Deactivate user"}">
                        <span class="material-symbols-outlined pointer-events-none text-[18px]">archive</span>
                    </button>
                ` : `
                    <button type="button" class="btn-reactivate-user standard-action-icon-btn" data-user-id="${attrValue(user.id)}" title="Reactivate user" aria-label="Reactivate user">
                        <span class="material-symbols-outlined pointer-events-none text-[18px]">unarchive</span>
                    </button>
                `}
                </div>
            </td>
        </tr>`;
    })
    .join("");

  container.innerHTML = `
        <div class="bg-white rounded-lg shadow overflow-hidden">
            <div class="settings-users-table-wrapper">
            <table class="settings-users-table w-full text-sm text-left table-fixed">
                <colgroup>
                    <col class="settings-users-col-user">
                    <col class="settings-users-col-role">
                    <col class="settings-users-col-permission">
                    ${PERMISSION_COLUMNS
                      .map(() => '<col class="settings-users-col-permission">')
                      .join("")}
                    <col class="settings-users-col-actions">
                </colgroup>
                <thead class="text-xs text-gray-700 uppercase bg-gray-100 border-b">
                    <tr>
                        <th class="py-3 px-4">User</th>
                        <th class="py-3 px-3 whitespace-nowrap">Role</th>
                        <th class="settings-users-permission-header border-l border-gray-300 py-3 text-center">Provider</th>
                        ${PERMISSION_COLUMNS
                          .map(
                            (column) => `
                        <th class="settings-users-permission-header border-l border-gray-300 py-3 text-center">${column.label}</th>`,
                          )
                          .join("")}
                        <th class="settings-users-actions-header border-l border-gray-300 py-3 text-center">Actions</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    ${rows}
                </tbody>
            </table>
            </div>
        </div>
        <div class="mt-3 flex justify-end">
            <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
                <input type="checkbox" id="show-inactive-users-toggle" class="${NATIVE_CHECKBOX_CLASSES}" ${showInactiveUsers ? "checked" : ""}>
                <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                    <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                </span>
                <span>Show inactive users</span>
            </label>
        </div>
        ${renderUserInvitesSection()}
        ${renderDeactivationDraftContainer()}
    `;
  setupUserRoleControls(container);
  setupUserDeactivationDraftControls(container);
  bindNativeFieldBehavior(container);
}

/**
 * Handles Users tab form and toggle changes.
 * @param {HTMLElement} target Event target.
 * @returns {boolean} True when the event was handled.
 */
export function handleUserSettingsChange(target) {
  if (handleUserInviteSettingsChange(target, { rerenderUsersTab })) {
    return true;
  }

  if (
    handleUserDeactivationSettingsChange(target, {
      rerenderUsersTab,
      updateSaveButton: updateSaveButtonHandler,
    })
  ) {
    return true;
  }

  if (target.matches("#show-inactive-users-toggle")) {
    showInactiveUsers = target.checked;
    rerenderUsersTab();
    return true;
  }

  if (
    !target.matches(".user-role-select") &&
    !target.matches(".user-perm-check") &&
    !target.matches(".user-app-provider-check")
  ) {
    return false;
  }

  const index = parseInt(target.dataset.index, 10);
  const user = tempUsersData[index];
  if (!user) {
    console.warn(
      `handleUserSettingsChange: User at index ${index} is undefined. Possible race condition or stale DOM.`,
    );
    return true;
  }

  if (target.matches(".user-role-select")) {
    user.role = target.value;
    normalizeAdminPermissions(user);
    rerenderUsersTab();
  } else if (target.matches(".user-app-provider-check")) {
    user.isAppProvider = target.checked;
  } else if (target.matches(".user-perm-check")) {
    const perm = target.dataset.perm;
    if (!user.permissions) {
      user.permissions = [];
    }

    if (target.checked) {
      if (!user.permissions.includes(perm)) {
        user.permissions.push(perm);
      }
    } else {
      user.permissions = user.permissions.filter((permission) => permission !== perm);
    }
    user.permissions = applyPermissionImplications(user.permissions);
    rerenderUsersTab();
  }

  updateSaveButtonHandler(true);
  return true;
}

/**
 * Syncs Users-tab sub-state from the current DOM before save.
 */
export function syncUserSettingsStateFromDom() {
  syncUserDeactivationSettingsStateFromDom();
}

/**
 * Applies Users-tab post-save state updates after a successful settings save.
 */
export function handleUserSettingsSaveSuccess() {
  handleUserDeactivationSettingsSaveSuccess();
}

/**
 * Resets local Users-tab UI state when leaving or cancelling settings.
 */
export function resetUserSettingsState() {
  showInactiveUsers = false;
  resetUserInviteSettingsState();
  resetUserDeactivationSettingsState();
}

/**
 * Rerenders the Users tab when live user invite data changes.
 */
export function syncUserSettingsFromLiveData() {
  if (state.currentView !== "settings" || !isUserSettingsTabActive()) {
    return;
  }
  rerenderUsersTab();
}

/**
 * Handles Users tab button clicks and modal actions.
 * @param {HTMLElement} target Event target.
 * @returns {Promise<boolean>} True when the event was handled.
 */
export async function handleUserSettingsButtonClick(target) {
  if (
    await handleUserInviteSettingsButtonClick(target, {
      rerenderUsersTab,
    })
  ) {
    return true;
  }

  const editUserButton = target.closest(".btn-edit-user");
  if (editUserButton) {
    const index = editUserButton.dataset.index;
    const user = tempUsersData[index];
    if (user) {
      openUserForm(user.id);
    }
    return true;
  }

  if (
    await handleUserDeactivationSettingsButtonClick(target, {
      rerenderUsersTab,
      updateSaveButton: updateSaveButtonHandler,
    })
  ) {
    return true;
  }

  return false;
}
